import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { 
  FaUser, FaEnvelope, FaPhone, FaCalendar, FaMapMarkerAlt,
  FaBuilding, FaClock, FaGraduationCap, FaSearch, FaFilter,
  FaEye, FaEdit, FaStar, FaDownload, FaPlus,
  FaCheckCircle, FaExclamationTriangle,
  FaTimes, FaSpinner, FaChartLine, FaTasks, FaAward
} from 'react-icons/fa';

const ManageInterns = () => {
  const { user } = EAuthData();
  const [interns, setInterns] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [showInternModal, setShowInternModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  
  // Filters and Search
  const [filters, setFilters] = useState({
    position: 'all',
    status: 'all',
    search: ''
  });

  // Performance Tracking
  const [performanceData, setPerformanceData] = useState({
    attendance: '',
    taskCompletion: '',
    quality: '',
    communication: '',
    initiative: '',
    notes: '',
    rating: 0
  });

  // Statistics
  const [stats, setStats] = useState({
    totalInterns: 0,
    activeInterns: 0,
    completedInterns: 0,
    averageRating: 0
  });

  // Fetch accepted applications (interns) and positions
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userd?.uid) return;

      try {
        setLoading(true);
        
        // Fetch positions
        const positionsRef = collection(db, 'positions');
        const positionsQuery = query(
          positionsRef,
          where('employer_id', '==', user.userd.uid)
        );
        const positionsSnapshot = await getDocs(positionsQuery);
        const positionsData = positionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPositions(positionsData);

        // Fetch accepted applications (interns)
        const applicationsRef = collection(db, 'applications');
        const applicationsQuery = query(
          applicationsRef,
          where('employerId', '==', user.userd.uid),
          where('status', '==', 'student_accepted'),
          orderBy('appliedAt', 'desc')
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        // Fetch additional intern data and performance records
        const internsData = await Promise.all(
          applicationsSnapshot.docs.map(async (doc) => {
            const applicationData = { id: doc.id, ...doc.data() };
            
            // Try to fetch performance data
            try {
              const performanceRef = collection(db, 'internPerformance');
              const performanceQuery = query(
                performanceRef,
                where('internId', '==', applicationData.studentId),
                where('employerId', '==', user.userd.uid),
                where('positionId', '==', applicationData.positionId)
              );
              const performanceSnapshot = await getDocs(performanceQuery);
              
              if (!performanceSnapshot.empty) {
                const performanceDoc = performanceSnapshot.docs[0];
                applicationData.performance = { id: performanceDoc.id, ...performanceDoc.data() };
              }
            } catch (error) {
              console.error('Error fetching performance data:', error);
            }
            
            return applicationData;
          })
        );
        
        setInterns(internsData);

        // Calculate statistics
        const totalInterns = internsData.length;
        const activeInterns = internsData.filter(intern => 
          intern.performance?.status !== 'completed'
        ).length;
        const completedInterns = internsData.filter(intern => 
          intern.performance?.status === 'completed'
        ).length;
        
        const ratingsSum = internsData.reduce((sum, intern) => {
          return sum + (intern.performance?.rating || 0);
        }, 0);
        const averageRating = totalInterns > 0 ? ratingsSum / totalInterns : 0;

        setStats({
          totalInterns,
          activeInterns,
          completedInterns,
          averageRating: Math.round(averageRating * 10) / 10
        });

      } catch (error) {
        console.error('Error fetching interns data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Filter interns
  const filteredInterns = interns.filter(intern => {
    // Position filter
    if (filters.position !== 'all' && intern.positionId !== filters.position) return false;
    
    // Status filter
    if (filters.status !== 'all') {
      const internStatus = intern.performance?.status || 'active';
      if (internStatus !== filters.status) return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return (
        intern.studentName?.toLowerCase().includes(searchTerm) ||
        intern.studentEmail?.toLowerCase().includes(searchTerm) ||
        intern.positionTitle?.toLowerCase().includes(searchTerm)
      );
    }
    
    return true;
  });

  // Save performance evaluation
  const savePerformance = async () => {
    if (!selectedIntern) return;

    try {
      const performanceDocData = {
        internId: selectedIntern.studentId,
        internName: selectedIntern.studentName,
        employerId: user.userd.uid,
        positionId: selectedIntern.positionId,
        positionTitle: selectedIntern.positionTitle,
        ...performanceData,
        lastUpdated: new Date().toISOString(),
        status: performanceData.status || 'active'
      };

      if (selectedIntern.performance?.id) {
        // Update existing performance record
        await updateDoc(doc(db, 'internPerformance', selectedIntern.performance.id), performanceDocData);
      } else {
        // Create new performance record
        const docRef = await addDoc(collection(db, 'internPerformance'), performanceDocData);
        performanceDocData.id = docRef.id;
      }

      // Update local state
      setInterns(prev => 
        prev.map(intern => 
          intern.id === selectedIntern.id 
            ? { ...intern, performance: performanceDocData }
            : intern
        )
      );

      setShowPerformanceModal(false);
      alert('Performance evaluation saved successfully!');
      
    } catch (error) {
      console.error('Error saving performance:', error);
      alert('Error saving performance evaluation. Please try again.');
    }
  };

  // Export interns data to CSV
  const exportInterns = () => {
    const csvData = filteredInterns.map(intern => ({
      'Intern Name': intern.studentName,
      'Email': intern.studentEmail,
      'Position': intern.positionTitle,
      'Start Date': new Date(intern.appliedAt).toLocaleDateString(),
      'Status': intern.performance?.status || 'Active',
      'Rating': intern.performance?.rating || 'Not Rated',
      'Attendance': intern.performance?.attendance || 'N/A',
      'Task Completion': intern.performance?.taskCompletion || 'N/A'
    }));
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(csvData[0]).join(",") + "\n"
      + csvData.map(row => Object.values(row).join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `interns_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'terminated': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={`h-4 w-4 ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
        />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Manage Interns</h1>
            <p className="text-gray-600">Track and manage your accepted interns</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={exportInterns}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <FaDownload className="mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Total Interns</h3>
                <p className="text-2xl font-bold text-indigo-600">{stats.totalInterns}</p>
              </div>
              <FaUser className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Active Interns</h3>
                <p className="text-2xl font-bold text-green-600">{stats.activeInterns}</p>
              </div>
              <FaCheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Completed</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.completedInterns}</p>
              </div>
              <FaAward className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Avg Rating</h3>
                <p className="text-2xl font-bold text-yellow-600">{stats.averageRating}/5</p>
              </div>
              <FaStar className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search interns..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            
            {/* Position Filter */}
            <select
              value={filters.position}
              onChange={(e) => setFilters(prev => ({ ...prev, position: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Positions</option>
              {positions.map(position => (
                <option key={position.id} value={position.id}>{position.name}</option>
              ))}
            </select>
            
            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        </div>

        {/* Interns Grid */}
        <div className="bg-white rounded-lg shadow-md">
          {filteredInterns.length === 0 ? (
            <div className="text-center py-12">
              <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No interns found</h3>
              <p className="text-gray-600">
                {interns.length === 0 
                  ? "No accepted interns yet."
                  : "No interns match your current filters."
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredInterns.map(intern => (
                <div key={intern.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow border border-gray-200">
                  {/* Intern Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="bg-orange-100 p-2 rounded-full mr-3">
                        <FaGraduationCap className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{intern.studentName}</h3>
                        <p className="text-sm text-gray-600">{intern.studentEmail}</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(intern.performance?.status || 'active')}`}>
                      {(intern.performance?.status || 'active').charAt(0).toUpperCase() + (intern.performance?.status || 'active').slice(1)}
                    </span>
                  </div>

                  {/* Position Info */}
                  <div className="mb-4">
                    <div className="flex items-center text-gray-600 mb-2">
                      <FaBuilding className="mr-2 h-4 w-4" />
                      <span className="text-sm font-medium">{intern.positionTitle}</span>
                    </div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <FaCalendar className="mr-2 h-4 w-4" />
                      <span className="text-sm">Started: {new Date(intern.appliedAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Performance Rating */}
                  {intern.performance?.rating && (
                    <div className="mb-4">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600 mr-2">Rating:</span>
                        <div className="flex">
                          {renderStars(intern.performance.rating)}
                        </div>
                        <span className="ml-2 text-sm text-gray-600">({intern.performance.rating}/5)</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedIntern(intern);
                        setShowInternModal(true);
                      }}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      <FaEye className="mr-1 h-3 w-3" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedIntern(intern);
                        setPerformanceData(intern.performance || {
                          attendance: '', taskCompletion: '', quality: '',
                          communication: '', initiative: '', notes: '', rating: 0, status: 'active'
                        });
                        setShowPerformanceModal(true);
                      }}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                    >
                      <FaChartLine className="mr-1 h-3 w-3" />
                      Evaluate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Intern Detail Modal */}
        {showInternModal && selectedIntern && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Intern Details</h2>
                  <button
                    onClick={() => setShowInternModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <p><strong>Name:</strong> {selectedIntern.studentName}</p>
                      <p><strong>Email:</strong> {selectedIntern.studentEmail}</p>
                      <p><strong>Student ID:</strong> {selectedIntern.studentInternID || 'N/A'}</p>
                      <p><strong>Start Date:</strong> {new Date(selectedIntern.appliedAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Position Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Position Information</h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <p><strong>Position:</strong> {selectedIntern.positionTitle}</p>
                      <p><strong>Status:</strong> 
                        <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedIntern.performance?.status || 'active')}`}>
                          {(selectedIntern.performance?.status || 'active').charAt(0).toUpperCase() + (selectedIntern.performance?.status || 'active').slice(1)}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Performance Overview */}
                {selectedIntern.performance && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-800">Attendance</h4>
                        <p className="text-blue-600">{selectedIntern.performance.attendance || 'Not rated'}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-medium text-green-800">Task Completion</h4>
                        <p className="text-green-600">{selectedIntern.performance.taskCompletion || 'Not rated'}</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-medium text-purple-800">Quality of Work</h4>
                        <p className="text-purple-600">{selectedIntern.performance.quality || 'Not rated'}</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h4 className="font-medium text-orange-800">Communication</h4>
                        <p className="text-orange-600">{selectedIntern.performance.communication || 'Not rated'}</p>
                      </div>
                      <div className="bg-cyan-50 p-4 rounded-lg">
                        <h4 className="font-medium text-cyan-800">Initiative</h4>
                        <p className="text-cyan-600">{selectedIntern.performance.initiative || 'Not rated'}</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-medium text-yellow-800">Overall Rating</h4>
                        <div className="flex items-center">
                          <div className="flex mr-2">
                            {renderStars(selectedIntern.performance.rating || 0)}
                          </div>
                          <span className="text-yellow-600">({selectedIntern.performance.rating || 0}/5)</span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedIntern.performance.notes && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-800 mb-2">Notes</h4>
                        <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedIntern.performance.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowInternModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowInternModal(false);
                      setPerformanceData(selectedIntern.performance || {
                        attendance: '', taskCompletion: '', quality: '',
                        communication: '', initiative: '', notes: '', rating: 0, status: 'active'
                      });
                      setShowPerformanceModal(true);
                    }}
                    className="flex items-center bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
                  >
                    <FaEdit className="mr-2" />
                    Update Performance
                  </button>
                  <button
                    onClick={() => window.open(`mailto:${selectedIntern.studentEmail}`, '_blank')}
                    className="flex items-center bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                  >
                    <FaEnvelope className="mr-2" />
                    Email Intern
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Evaluation Modal */}
        {showPerformanceModal && selectedIntern && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Performance Evaluation</h2>
                  <button
                    onClick={() => setShowPerformanceModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Intern Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800">{selectedIntern.studentName}</h3>
                    <p className="text-gray-600">{selectedIntern.positionTitle}</p>
                  </div>

                  {/* Performance Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Attendance</label>
                      <select
                        value={performanceData.attendance}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, attendance: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select rating</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Task Completion</label>
                      <select
                        value={performanceData.taskCompletion}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, taskCompletion: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select rating</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quality of Work</label>
                      <select
                        value={performanceData.quality}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, quality: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select rating</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Communication</label>
                      <select
                        value={performanceData.communication}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, communication: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select rating</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Initiative</label>
                      <select
                        value={performanceData.initiative}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, initiative: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select rating</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Average">Average</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={performanceData.status}
                        onChange={(e) => setPerformanceData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="on-hold">On Hold</option>
                        <option value="terminated">Terminated</option>
                      </select>
                    </div>
                  </div>

                  {/* Overall Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Overall Rating</label>
                    <div className="flex items-center space-x-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setPerformanceData(prev => ({ ...prev, rating: star }))}
                          className="focus:outline-none"
                        >
                          <FaStar
                            className={`h-6 w-6 ${
                              star <= performanceData.rating ? 'text-yellow-400' : 'text-gray-300'
                            } hover:text-yellow-400 transition-colors`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-gray-600">({performanceData.rating}/5)</span>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={performanceData.notes}
                      onChange={(e) => setPerformanceData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Additional notes about performance..."
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowPerformanceModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={savePerformance}
                    className="flex items-center bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
                  >
                    <FaCheckCircle className="mr-2" />
                    Save Evaluation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageInterns;